import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { usePursuit } from '../contexts/PursuitContext';
import { usePerspective } from '../contexts/PerspectiveContext';
import { useFeatures } from '../contexts/FeatureContext';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import SidebarIconRail from './SidebarIconRail';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  Kanban,
  BookOpen,  Settings,
  LogOut,
  FileCode2,
  Briefcase,
  FileText,
  PieChart,
  Target,
  FileCheck,
  UsersRound,
  ClipboardList,
  ArrowLeft,
  Contact,
  Clock,
  Sparkles,
  Rocket,
  Bot,
  FolderOpen,
  DollarSign,
  UserCog,
  HelpCircle,
  ShieldCheck,
  ScrollText,
  MessageCircleQuestion,
  ListChecks
} from 'lucide-react';
import { Button } from './ui/button';
import FeatureLockedModal from './modals/FeatureLockedModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Features that require Growth plan or higher
const PRO_FEATURES = {
  'teaming': true,
  'gap_analysis': true, 
  'proposal_workspace': true,
  'ai_scoring': true
};

// ============================================
// GLOBAL MENU CONFIGURATIONS (Management)
// ============================================

// TIER 1: Super Admin (Platform Owner)
// Unified view: Dashboard (Strategic KPIs), Contractor Lifecycle (Single Source of Truth), Consultant Mgmt, Asset Library, Audit Logs, System Docs, AI Support, Roadmap
// `group` drives the divider clusters in the icon-rail nav (SidebarIconRail) —
// oversight (people/queues) / content (playbooks & records) / platform (system config).
const SUPER_ADMIN_MENU = [
  { id: 'overview', label: 'Dashboard', icon: PieChart, path: '/admin/dashboard', group: 'oversight' },
  { id: 'lifecycle', label: 'Contractor Lifecycle', icon: Users, path: '/admin/lifecycle', group: 'oversight' },
  { id: 'consultants', label: 'Consultant Management', icon: Briefcase, path: '/admin/consultants', group: 'oversight' },
  { id: 'users', label: 'User Management', icon: UserCog, path: '/admin/users', group: 'oversight' },
  { id: 'agencies', label: 'Agency Playbook', icon: Building2, path: '/admin/agencies', group: 'content' },
  { id: 'assets', label: 'Asset Library', icon: BookOpen, path: '/admin/assets', group: 'content' },
  { id: 'audit', label: 'Audit Logs', icon: ClipboardList, path: '/admin/audit', group: 'content' },
  { id: 'docs', label: 'System Docs', icon: FileCode2, path: '/admin/docs', group: 'content' },
  { id: 'ai-config', label: 'AI Configuration', icon: Bot, path: '/admin/ai-config', group: 'platform' },
  { id: 'features', label: 'Feature Releases', icon: Rocket, path: '/admin/features', group: 'platform' },
  { id: 'roadmap', label: 'Product Roadmap', icon: Sparkles, path: '/admin/roadmap', group: 'platform' },
  { id: 'costs', label: 'Development Costs', icon: DollarSign, path: '/admin/costs', group: 'platform' },
];

// TIER 2: Consultant - GLOBAL Menu
// Note: "Client Profiles & Contacts" removed - now accessible via drill-down from Client Directory
const CONSULTANT_GLOBAL_MENU = [
  { id: 'portfolio', label: 'Client Portfolio', icon: LayoutDashboard, path: '/dashboard' },
  { id: 'sentinel', label: 'Marketplace Sentinel', icon: Target, path: '/sentinel', feature: 'sentinel' },
  { id: 'pipeline', label: 'Master Pursuit Pipeline', icon: Kanban, path: '/pipeline' },
  { id: 'clients', label: 'Client Directory', icon: Building2, path: '/clients' },
  // v3.18.6 — Renamed "Knowledge Base" → "Reusable Asset Library" to disambiguate
  // from per-client Client Library on the Client Profile and from per-pursuit
  // semantic search on Manage Documents. Same route, same data.
  { id: 'knowledge-base', label: 'Reusable Asset Library', icon: BookOpen, path: '/knowledge-base' },
  { id: 'help', label: 'Help & Support', icon: HelpCircle, path: '/help' },
  { id: 'settings', label: 'Consultancy Settings', icon: Settings, path: '/settings' },
];

// TIER 3: Contractor Admin - GLOBAL Menu
const CONTRACTOR_ADMIN_GLOBAL_MENU = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { id: 'sentinel', label: 'Marketplace Sentinel', icon: Target, path: '/sentinel', feature: 'sentinel' },
  { id: 'pipeline', label: 'Pursuit Pipeline', icon: Kanban, path: '/pipeline' },
  { id: 'knowledge-base', label: 'Reusable Asset Library', icon: BookOpen, path: '/knowledge-base' },
  { id: 'help', label: 'Help & Support', icon: HelpCircle, path: '/help' },
  { id: 'settings', label: 'Workspace Settings', icon: Settings, path: '/settings' },
];

// Bid Manager - GLOBAL Menu (No Client Directory, has Dashboard access)
const BID_MANAGER_GLOBAL_MENU = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { id: 'pipeline', label: 'Pursuit Pipeline', icon: Kanban, path: '/pipeline' },
  { id: 'help', label: 'Help & Support', icon: HelpCircle, path: '/help' },
];

// Reviewer - GLOBAL Menu (Limited access with Dashboard)
const REVIEWER_GLOBAL_MENU = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { id: 'pipeline', label: 'Assigned Pursuits', icon: Kanban, path: '/pipeline', readOnly: true },
  { id: 'help', label: 'Help & Support', icon: HelpCircle, path: '/help' },
];

// ============================================
// PURSUIT-SPECIFIC MENU (Execution)
// ============================================

// Consultant/Bid Manager - When inside a pursuit
// v3.18.10 — Re-ordered to mirror the federal capture lifecycle.
// v3.18.16 — Compliance now splits into two distinct lifecycle slots:
//   • Compliance Matrix (Section L/M shred) — between Strategy and Proposal
//   • Submission       (post-draft format & delivery) — final stage
// Both menu entries route to the same Compliance Tracker page but open
// different tabs via the existing `?tab=` query param. This mirrors how
// Shipley / APMP teams treat compliance as two artifacts: a planning matrix
// that drives drafting, and a submission-day gate.
const PURSUIT_SPECIFIC_MENU = [
  { id: 'intake',               label: 'Opportunity Intake',     icon: ClipboardList,          path: '/intake',               feature: 'opportunity_intake' },
  { id: 'ingestion',            label: 'Document Ingestion',     icon: FolderOpen,             path: '/ingestion',            feature: 'document_ingestion' },
  { id: 'capability_statement', label: 'Capability Statement',   icon: ScrollText,             path: '/capability-statement', rfiOnly: true },
  { id: 'agency_questions',     label: 'Questions for Agency',   icon: MessageCircleQuestion,  path: '/questions' },
  { id: 'gonogo',               label: 'Go/No-Go Dashboard',     icon: Target,                 path: '/gonogo',               feature: 'go_no_go' },
  { id: 'gaps',                 label: 'Gap Analysis & Teaming', icon: UsersRound,             path: '/gap-analysis',         proFeature: 'gap_analysis', feature: 'gap_analysis' },
  { id: 'strategy',             label: 'Strategy',               icon: Sparkles,               path: '/strategy' },
  // v3.18.16 — Pre-draft Compliance Check (Section L/M shred → author plan).
  // Renamed from "Compliance Matrix" — the matrix itself now lives in Manage Documents.
  { id: 'compliance_matrix',    label: 'Compliance Check',       icon: ListChecks,             path: '/compliance' },
  { id: 'proposal',             label: 'Proposal Workspace',     icon: FileText,               path: '/proposal-workspace',   proFeature: 'proposal_workspace', feature: 'proposal_workspace' },
  // v3.18.16 — Submission (post-draft format & delivery checks).
  { id: 'submission',           label: 'Submission',             icon: ShieldCheck,            path: '/compliance?tab=submission-day', feature: 'compliance_tracker' },
];

// Reviewer - Limited pursuit access
const REVIEWER_PURSUIT_MENU = [
  { id: 'gonogo', label: 'Go/No-Go Dashboard', icon: Target, path: '/gonogo', feature: 'go_no_go' },
  { id: 'compliance', label: 'Compliance Tracker', icon: ShieldCheck, path: '/compliance', feature: 'compliance_tracker' },
  // v3.11.8 — Submission Guide merged into Compliance Tracker (Submission Day tab).
];

function getGlobalMenuForRole(role) {
  switch (role) {
    case ROLES.SUPER_ADMIN:
      return SUPER_ADMIN_MENU;
    case ROLES.CONSULTANT:
    case ROLES.CONSULTANT_ASSOCIATE:
      return CONSULTANT_GLOBAL_MENU;
    case ROLES.CONTRACTOR_ADMIN:
      return CONTRACTOR_ADMIN_GLOBAL_MENU;
    case ROLES.BID_MANAGER:
      return BID_MANAGER_GLOBAL_MENU;
    case ROLES.REVIEWER:
      return REVIEWER_GLOBAL_MENU;
    default:
      return [];
  }
}

function getPursuitMenuForRole(role) {
  switch (role) {
    case ROLES.CONSULTANT:
    case ROLES.CONSULTANT_ASSOCIATE:
    case ROLES.CONTRACTOR_ADMIN:
    case ROLES.BID_MANAGER:
      return PURSUIT_SPECIFIC_MENU;
    case ROLES.REVIEWER:
      return REVIEWER_PURSUIT_MENU;
    default:
      return [];
  }
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const { user, authToken, logout } = useAuth();
  const { activePursuit, exitPursuit, clearPursuitState, isInPursuitContext } = usePursuit();
  const { getEffectiveRole, isInMockView } = usePerspective();
  const { isFeatureEnabled, isLoaded: featuresLoaded } = useFeatures();
  
  // Feature gating state
  const [featureAccess, setFeatureAccess] = useState({});
  const [lockedFeature, setLockedFeature] = useState(null);
  const [showLockedModal, setShowLockedModal] = useState(false);

  // v3.18.10/16 — "You are here" highlight in the pursuit menu. Maps the
  // pursuit's lifecycle status to the menu item that owns that stage.
  // Status enum (mongo_pursuits.py): INTAKE, QUALIFICATION, STRATEGY,
  // DRAFTING, SUBMITTED, WON, LOST, NO_BID.
  // v3.18.16: STRATEGY now highlights the Strategy item; the Compliance
  // Matrix lives between Strategy and Drafting but doesn't have its own
  // status — users naturally land there from Strategy's "next →" footer.
  // SUBMITTED still highlights the Submission item (renamed from
  // Compliance Tracker).
  const STATUS_TO_MENU_ID = {
    INTAKE:        'intake',
    QUALIFICATION: 'gonogo',
    STRATEGY:      'strategy',
    DRAFTING:      'proposal',
    SUBMITTED:     'submission',
    // WON / LOST / NO_BID are terminal — no highlight.
  };
  const [pursuitStage, setPursuitStage] = useState(null);
  useEffect(() => {
    if (!isInPursuitContext || !activePursuit?.id || !authToken) {
      setPursuitStage(null);
      return;
    }
    // Prefer status from the context if a caller passed it in.
    if (activePursuit?.status && STATUS_TO_MENU_ID[activePursuit.status]) {
      setPursuitStage(STATUS_TO_MENU_ID[activePursuit.status]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/pursuits/${activePursuit.id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const mapped = STATUS_TO_MENU_ID[data?.status];
        if (!cancelled) setPursuitStage(mapped || null);
      } catch (_) {
        if (!cancelled) setPursuitStage(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isInPursuitContext, activePursuit?.id, activePursuit?.status, authToken]);

  // Use effective role for UI rendering (respects perspective switching)
  const effectiveRole = getEffectiveRole();
  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN && !isInMockView;
  const isConsultant = effectiveRole === ROLES.CONSULTANT;
  
  // Get menu based on effective role (for perspective switching)
  const rawGlobalMenuItems = getGlobalMenuForRole(effectiveRole);
  // Filter menu items by feature flag. Items without a `feature` key
  // are treated as infrastructure (always visible). Items with a `feature`
  // key are hidden when that feature is disabled in the current mode.
  // Until the feature config has loaded, we keep items visible to avoid flicker.
  // v3.13.0 — `rfiOnly` items (e.g. Capability Statement) stay visible across
  // all pursuits; the page itself shows a "set notice_type=RFI" empty state.
  // This keeps the feature discoverable instead of hiding it behind a field
  // most users haven't filled in yet.
  const filterByFeature = (items) =>
    items.filter((item) => !item.feature || !featuresLoaded || isFeatureEnabled(item.feature));
  const globalMenuItems = filterByFeature(rawGlobalMenuItems);

  // Check if user has access to pro features (Growth plan or higher)
  const hasProAccess = isSuperAdmin || isConsultant || 
    (featureAccess.plan && ['GROWTH', 'ENTERPRISE'].includes(featureAccess.plan));

  // Fetch gating status on mount
  useEffect(() => {
    const fetchGatingStatus = async () => {
      if (!authToken || isSuperAdmin || isConsultant) {
        setFeatureAccess({ plan: 'ENTERPRISE' }); // Full access for admin/consultant
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/trial/gating-status`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (response.ok) {
          const data = await response.json();
          setFeatureAccess(data);
        }
      } catch (err) {
        console.error('Failed to fetch gating status:', err);
      }
    };
    
    fetchGatingStatus();
  }, [authToken, isSuperAdmin, isConsultant]);

  const handleNavigation = (path, isPursuitItem = false, proFeature = null) => {
    // Check if feature is locked
    if (proFeature && !hasProAccess) {
      setLockedFeature(proFeature);
      setShowLockedModal(true);
      return;
    }
    
    if (isSuperAdmin) {
      navigate(path);
    } else if (isPursuitItem && activePursuit) {
      navigate(`/${workspaceId}/pursuit/${activePursuit.id}${path}`);
    } else {
      navigate(`/${workspaceId}${path}`);
    }
  };

  const handleExitPursuit = () => {
    exitPursuit();
    navigate(`/${workspaceId}/pipeline`);
  };

  const handleLogout = () => {
    clearPursuitState(); // Clear pursuit state first
    logout();
    navigate('/');
  };

  // v3.18.16 — `path` may include a `?tab=` query so we differentiate two
  // sidebar entries that share the same route (Compliance Matrix vs.
  // Submission). Split path|query and compare each independently.
  const isActive = (rawPath, isPursuitItem = false) => {
    const [pathOnly, query] = String(rawPath).split('?');
    const tab = new URLSearchParams(query || '').get('tab');
    const currentTab = new URLSearchParams(location.search || '').get('tab');
    let basePath;
    if (isSuperAdmin) {
      basePath = pathOnly;
    } else if (isPursuitItem && activePursuit) {
      basePath = `/${workspaceId}/pursuit/${activePursuit.id}${pathOnly}`;
    } else {
      basePath = `/${workspaceId}${pathOnly}`;
    }
    if (location.pathname !== basePath) return false;
    // If this menu entry binds to a specific tab, the current URL's tab
    // must match (a missing tab means we're on the default tab — Matrix).
    if (tab) return currentTab === tab;
    return !currentTab || currentTab === 'checklist' || currentTab === 'matrix';
  };

  // In pursuit focus mode, collapse to a slim icon rail so the user gets
  // maximum workspace width. The top bar carries the lifecycle stepper.
  if (isInPursuitContext) {
    return (
      <aside className="sidebar sidebar-slim flex flex-col items-center py-3 gap-3" data-testid="app-sidebar">
        {/* FedBD icon */}
        <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">F</span>
        </div>
        {/* Divider */}
        <div className="w-6 h-px bg-slate-700" />
        {/* Back to Pipeline */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExitPursuit}
          className="w-9 h-9 p-0 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
          title="Back to Pipeline"
          data-testid="exit-pursuit-btn-slim"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
      </aside>
    );
  }

  // Every role gets the dock-style icon rail — grouped clusters for the
  // longer Super Admin menu, a single flat cluster for everyone else.
  // Consultants/Associates get the workspace switcher as a compact rail
  // icon (same dropdown content, just a narrow trigger) in place of the
  // old wide dropdown that only fit in the full-width sidebar.
  const showWorkspaceSwitcher = isConsultant || user?.role === ROLES.CONSULTANT_ASSOCIATE;
  return (
    <>
      <SidebarIconRail
        items={globalMenuItems}
        isActive={isActive}
        onNavigate={(path) => handleNavigation(path)}
        user={user}
        onLogout={handleLogout}
        topExtra={showWorkspaceSwitcher ? <WorkspaceSwitcher compact /> : null}
      />

      {/* Feature Locked Modal */}
      <FeatureLockedModal
        isOpen={showLockedModal}
        onClose={() => setShowLockedModal(false)}
        featureKey={lockedFeature}
        onRequestUpgrade={() => {
          // Navigate to pricing or contact sales
          navigate('/pricing');
        }}
      />
    </>
  );
}
